import React, { useEffect, useState } from 'react';
import axios from 'axios';
import RateCardForm from './RateCardForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import SaveButton from '../../../shared/SaveButton';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import { Pill } from '../../../shared/Pill';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { formatDate } from '../../../shared/tenantClock';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { askConfirm } from '../../../shared/AppDialogs';

const sectionHeaderStyle = { margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 };
const sectionStyle = { margin: '18px 24px 0', padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', maxWidth: 720 };
const HELP = { fontSize: 12, color: '#888', marginTop: 4 };
const th = { padding: '8px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #b3bbc7', textAlign: 'left' };
const td = { padding: '6px 8px', fontSize: 14 };

const BAND_OPTIONS = [0, 1, 2, 3, 4, 5].map((b) => ({ value: b, label: b === 0 ? 'Band 0 — standard rates' : `Band ${b}` }));

// Each Rate Card's standing on `today`: the one in force (latest Effective
// From on or before today), Upcoming (after today), or Superseded.
function cardStatus(cards, today) {
  const inForce = cards.filter((c) => c.effective_from <= today).sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1))[0];
  return (card) => {
    if (card.effective_from > today) return { label: 'Upcoming', color: '#1976d2', background: '#eaf2fb' };
    if (inForce && card.id === inForce.id) return { label: 'In force', color: '#1a7f4b', background: 'rgba(26, 127, 75, 0.12)' };
    return { label: 'Superseded', color: '#5a6b85', background: '#dde3ea' };
  };
}

// Sales › Australia Post (issue #328): the Australia Post methods that
// price shipping on a Quote and at Ship, side by side, and which is the
// default; the MyPost Business savings band every method prices at; and
// the dated MyPost Business Rate Cards. The PAC API key is on Platform ›
// API Integrations.
function AustraliaPostPage() {
  const { canCreate, canUpdate, canDelete } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [form, setForm] = useState(null); // { card, isNew }
  const [formLoading, setFormLoading] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get('/api/australia-post-pricing');
      const d = res.data.data;
      setData(d);
      setSettings({
        band: d.band,
        default_method: d.default_method,
        active: Object.fromEntries(d.methods.map((m) => [m.key, m.active])),
      });
    } catch (err) {
      showError(err, 'Failed to load Australia Post settings');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  if (!data || !settings) return null;

  const setActive = (key, on) => setSettings((s) => {
    const active = { ...s.active, [key]: on };
    const firstOn = data.methods.find((m) => active[m.key])?.key || null;
    const keepDefault = s.default_method && active[s.default_method];
    return { ...s, active, default_method: keepDefault ? s.default_method : firstOn };
  });

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put('/api/australia-post-pricing/settings', {
        band: settings.band,
        rate_card_active: !!settings.active.rate_card,
        pac_active: !!settings.active.pac,
        default_method: settings.default_method,
      });
      await load();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSavingSettings(false);
  };

  const saveCard = async (draft) => {
    setFormLoading(true);
    try {
      if (form.isNew) await axios.post('/api/australia-post-pricing/rate-cards', draft);
      else await axios.put(`/api/australia-post-pricing/rate-cards/${form.card.id}`, draft);
      setForm(null);
      await load();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  const deleteCard = async (card) => {
    if (!(await askConfirm(`Delete the Rate Card effective from ${formatDate(card.effective_from)}? Prices already selected on Quotes and Shipments stay as they are.`, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/australia-post-pricing/rate-cards/${card.id}`);
      await load();
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  const statusOf = cardStatus(data.rate_cards, data.today);
  const newest = data.rate_cards[0] || null;
  const busy = !!form;

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Australia Post</h2>
      </div>

      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Methods</h4>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
          Each method switched on prices shipping side by side on a Quote’s Shipping Estimate and at Ship. The default method’s price is selected to start with.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Method</th>
              <th style={{ ...th, width: 90 }}>On</th>
              <th style={{ ...th, width: 90, textAlign: 'center' }}>Default</th>
            </tr>
          </thead>
          <tbody>
            {data.methods.map((m) => (
              <tr key={m.key}>
                <td style={td}>
                  {m.name}
                  {!m.available && <div style={{ fontSize: 12, color: '#888' }}>Not yet available</div>}
                  {m.key === 'pac' && <div style={{ fontSize: 12, color: '#888' }}>Its key is on Platform › API Integrations.</div>}
                </td>
                <td style={td}>
                  <ToggleSwitch
                    checked={!!settings.active[m.key]}
                    onChange={(on) => setActive(m.key, on)}
                    disabled={!m.available || !canUpdate}
                    title={m.available ? `Price shipping with ${m.name}` : 'Not yet available'}
                  />
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <input
                    type="radio"
                    name="default-method"
                    checked={settings.default_method === m.key}
                    disabled={!m.available || !settings.active[m.key] || !canUpdate}
                    onChange={() => setSettings((s) => ({ ...s, default_method: m.key }))}
                    title={settings.active[m.key] ? 'Select this method’s price to start with' : 'Switch it on first'}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 16 }}>
          <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>MyPost Business savings band</label>
          <select
            className="styled-input"
            value={settings.band}
            onChange={(e) => setSettings((s) => ({ ...s, band: Number(e.target.value) }))}
            disabled={!canUpdate}
            style={{ width: 240 }}
          >
            {BAND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div style={HELP}>
            Shown on the Send and Save tab in MyPost Business. It changes with your spend, so update it here when it does. Every method prices at this band. Prices already selected on Quotes and Shipments stay as they are.
          </div>
        </div>

        {canUpdate && (
          <div style={{ marginTop: 14 }}>
            <SaveButton type="button" onClick={saveSettings} loading={savingSettings} />
          </div>
        )}
      </div>

      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h4 style={{ ...sectionHeaderStyle, margin: 0 }}>MyPost Business Rate Cards</h4>
          {canCreate && (
            <button
              type="button"
              onClick={() => setForm({ card: newest, isNew: true })}
              disabled={busy}
              title={busy ? 'Finish editing first' : 'Add a Rate Card, starting from the newest one'}
              style={{ padding: '6px 14px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
            >
              Add Rate Card
            </button>
          )}
        </div>
        <div style={{ fontSize: 13, color: '#666', margin: '8px 0 10px' }}>
          From Australia Post’s MyPost Business Rates and Savings Guide. A price uses the Rate Card in force on the day it’s worked out: the day estimated on a Quote, the day shipped at Ship.
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead className="list-head">
            <tr>
              <th style={{ ...th, width: 130 }}>Effective From</th>
              <th style={th}>Name</th>
              <th style={{ ...th, width: 110, textAlign: 'center' }}>Status</th>
              <th style={{ ...th, width: 50 }} />
            </tr>
          </thead>
          <tbody>
            {data.rate_cards.length === 0 ? (
              <tr><td colSpan={4} style={{ ...td, textAlign: 'center', background: '#f6f8fa', fontSize: 13 }}>No Rate Cards. The Rate Card method can’t price anything until one is added.</td></tr>
            ) : data.rate_cards.map((card) => {
              const status = statusOf(card);
              return (
                <tr
                  key={card.id}
                  className="list-row"
                  onClick={() => { if (!busy && canUpdate) setForm({ card, isNew: false }); }}
                  style={{ cursor: busy || !canUpdate ? 'default' : 'pointer' }}
                  title={busy || !canUpdate ? undefined : 'Click to edit'}
                >
                  <td style={td}>{formatDate(card.effective_from)}</td>
                  <td style={td}>{card.name || '—'}</td>
                  <td style={{ ...td, textAlign: 'center' }}><Pill color={status.color} background={status.background}>{status.label}</Pill></td>
                  <td style={{ ...td, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <RowActionsMenu
                      items={[
                        { key: 'edit', label: 'Edit', disabled: busy, disabledTitle: busy ? 'Finish editing first' : undefined, onClick: () => setForm({ card, isNew: false }) },
                        { key: 'duplicate', label: 'Copy to a new Rate Card', disabled: busy, disabledTitle: busy ? 'Finish editing first' : undefined, onClick: () => setForm({ card, isNew: true }) },
                        ...(canDelete ? [{ key: 'delete', label: 'Delete', danger: true, disabled: busy, disabledTitle: busy ? 'Finish editing first' : undefined, onClick: () => deleteCard(card) }] : []),
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {form && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8, maxWidth: 720 }}>
          <div style={{ fontWeight: 600, marginBottom: 10 }}>{form.isNew ? 'New Rate Card' : `Rate Card effective from ${formatDate(form.card.effective_from)}`}</div>
          <RateCardForm
            key={`${form.isNew ? 'new' : 'edit'}-${form.card?.id ?? 'blank'}`}
            initial={form.card}
            isNew={form.isNew}
            onSave={saveCard}
            onCancel={() => setForm(null)}
            loading={formLoading}
          />
        </div>
      )}
    </div>
  );
}

export default AustraliaPostPage;
